import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, Users, Activity, Gauge, Zap, AlertTriangle } from "lucide-react";

const Delta = ({ now, prev, suffix = "" }) => {
  if (now == null || prev == null) return <span className="text-xs text-slate-400">—</span>;
  const d = now - prev;
  const Icon = d > 0 ? TrendingUp : d < 0 ? TrendingDown : Minus;
  const tone = d > 0 ? "text-emerald-700 bg-emerald-50" : d < 0 ? "text-red-700 bg-red-50" : "text-slate-500 bg-slate-100";
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${tone}`}><Icon className="w-3 h-3" />{d > 0 ? "+" : ""}{d}{suffix}</span>;
};

const PERIODS = [["week", "7 days"], ["fortnight", "14 days"], ["month", "30 days"]];

export function TeamPulse({ team, metrics, period, onPeriod }) {
  const tiles = [
    { icon: Activity, label: "Practice calls", value: metrics.sessions, delta: <Delta now={metrics.sessions} prev={metrics.prev_sessions} />, tone: "emerald" },
    { icon: Gauge, label: "Avg score", value: metrics.avg_score ?? "—", delta: <Delta now={metrics.avg_score} prev={metrics.prev_avg} />, tone: "cyan" },
    { icon: Zap, label: "XP earned", value: metrics.xp.toLocaleString(), delta: <Delta now={metrics.xp} prev={metrics.prev_xp} />, tone: "amber" },
    { icon: Users, label: "Active reps", value: `${metrics.active_reps}/${metrics.reps}`, delta: null, tone: "slate" },
    { icon: AlertTriangle, label: "Need a nudge", value: metrics.at_risk, delta: null, tone: metrics.at_risk ? "red" : "slate" },
  ];
  const tones = { emerald: "text-emerald-700 bg-emerald-50", cyan: "text-cyan-700 bg-cyan-50", amber: "text-amber-700 bg-amber-50", slate: "text-slate-600 bg-slate-100", red: "text-red-700 bg-red-50" };
  return (
    <section className="rounded-3xl border border-slate-200/80 bg-white shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] p-5 sm:p-6" data-testid="team-pulse">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Team pulse</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" data-testid="manager-team-name">{team.name}</h1>
          <p className="text-sm text-slate-500 mt-1">Invite code <span className="font-mono font-bold text-slate-900">{team.code}</span> · compared with the previous {period.days} days</p>
        </div>
        <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="period-picker">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => onPeriod(k)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${period.key === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid={`period-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3">
        {tiles.map((t, i) => (
          <motion.div key={t.label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 * i }}
            className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-4" data-testid={`pulse-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tones[t.tone]}`}><t.icon className="w-4 h-4" /></div>
            <div className="text-2xl sm:text-3xl font-extrabold tracking-tight font-display text-slate-900 mt-3">{t.value}</div>
            <div className="flex items-center justify-between gap-2 mt-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t.label}</span>{t.delta}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
